// src/context/DataContext.js
import React, { createContext, useContext, useEffect } from "react";
import { useServicesModule } from "./modules/servicesModule";
import { useEventsModule } from "./modules/eventsModule";
import { useTeamMembersModule } from "./modules/teamMembersModule";
import { useImagesModule } from "./modules/imagesModule";

const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  // Initialize all modules
  const servicesModule = useServicesModule();
  const eventsModule = useEventsModule();
  const teamMembersModule = useTeamMembersModule();
  const imagesModule = useImagesModule();

  // Load initial data for all modules
  useEffect(() => {
    const loadAllData = async () => {
      // Load data in parallel
      await Promise.all([
        servicesModule.loadInitialServices(),
        eventsModule.loadInitialEvents(),
        teamMembersModule.loadInitialTeamMembers(),
        imagesModule.loadInitialImages(),
      ]);
    };

    loadAllData();
  }, []);

  // Calculate combined loading state
  const isLoading =
    servicesModule.servicesLoading ||
    eventsModule.eventsLoading ||
    teamMembersModule.teamMembersLoading ||
    imagesModule.imagesLoading;

  // Combine all module values
  const value = {
    // Services module
    services: servicesModule.services,
    servicesLoading: servicesModule.servicesLoading,
    refreshServices: servicesModule.refreshServices,
    addService: servicesModule.addService,
    updateService: servicesModule.updateService,
    removeService: servicesModule.removeService,

    // Events module
    events: eventsModule.events,
    eventsLoading: eventsModule.eventsLoading,
    refreshEvents: eventsModule.refreshEvents,
    addEvent: eventsModule.addEvent,
    updateEvent: eventsModule.updateEvent,
    removeEvent: eventsModule.removeEvent,

    // Team Members module
    teamMembers: teamMembersModule.teamMembers,
    teamMembersLoading: teamMembersModule.teamMembersLoading,
    refreshTeamMembers: teamMembersModule.refreshTeamMembers,
    addTeamMember: teamMembersModule.addTeamMember,
    updateTeamMember: teamMembersModule.updateTeamMember,
    removeTeamMember: teamMembersModule.removeTeamMember,

    // Images module
    images: imagesModule.images,
    imagesLoading: imagesModule.imagesLoading,
    uploadProgress: imagesModule.uploadProgress,
    refreshImages: imagesModule.refreshImages,
    addImage: imagesModule.addImage,
    updateImage: imagesModule.updateImage,
    removeImage: imagesModule.removeImage,
    getImageById: imagesModule.getImageById,
    getImageByKey: imagesModule.getImageByKey,
    getImageUrl: imagesModule.getImageUrl,

    // Combined loading state
    isLoading,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};
